import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, List, Spin, Button, Row, Col, DatePicker, Space, Typography, Radio } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { fetchShopifyStores } from '../../ShopifySettings/actions';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const SyncShopifyProductsModal = ({ visible, onOk, onCancel, loading }) => {
  const dispatch = useDispatch();
  const { shopifyStores, loading: storesLoading } = useSelector(state => state.shopifySettings || { shopifyStores: [], loading: false });
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterType, setFilterType] = useState('created'); // 'created' hoặc 'updated'
  const [createdDateRange, setCreatedDateRange] = useState([]);
  const [updatedDateRange, setUpdatedDateRange] = useState([]);
  const [syncType, setSyncType] = useState('PRODUCTS'); // 'PRODUCTS' hoặc 'PRODUCTS_AND_VARIANTS'

  useEffect(() => {
    if (visible) {
      // Fetch Shopify stores khi modal mở
      dispatch(fetchShopifyStores());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (visible && shopifyStores && shopifyStores.length > 0) {
      setSelectedIds(shopifyStores.map(store => store.id)); // Mặc định chọn hết
      setFilterType('created');
      setCreatedDateRange([]); // Reset date range khi mở modal
      setUpdatedDateRange([]);
      setSyncType('PRODUCTS');
    }
  }, [visible, shopifyStores]);

  const onChange = (checkedValues) => {
    setSelectedIds(checkedValues);
  };

  const handleFilterTypeChange = (e) => {
    const newFilterType = e.target.value;
    setFilterType(newFilterType);
    
    // Clear opposite filter when switching
    if (newFilterType === 'created') {
      setUpdatedDateRange([]);
    } else {
      setCreatedDateRange([]);
    }
  };

  const handleCreatedDateRangeChange = (dates) => {
    setCreatedDateRange(dates);
  };

  const handleUpdatedDateRangeChange = (dates) => {
    setUpdatedDateRange(dates);
  };

  const handleSyncTypeChange = (e) => {
    setSyncType(e.target.value);
  };

  const handleOk = () => {
    const syncData = {
      selectedIds,
      syncType
    };

    // Chỉ thêm filter tương ứng với loại đã chọn
    if (filterType === 'created' && createdDateRange && createdDateRange.length === 2) {
      syncData.createdAfter = createdDateRange[0].toISOString();
      syncData.createdBefore = createdDateRange[1].toISOString();
    } else if (filterType === 'updated' && updatedDateRange && updatedDateRange.length === 2) {
      syncData.updatedAfter = updatedDateRange[0].toISOString();
      syncData.updatedBefore = updatedDateRange[1].toISOString();
    }

    onOk(syncData);
  };

  return (
    <Modal
      title="Select Shopify Stores to sync products"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Sync"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
    >
      {storesLoading || shopifyStores.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Loading stores..." />
        </div>
      ) : (
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Sync Type Selection */}
            <div>
              <Text strong>Sync type:</Text>
              <br />
              <Radio.Group
                value={syncType}
                onChange={handleSyncTypeChange}
                style={{ marginTop: 8 }}
              >
                <Radio value="PRODUCTS">Products only</Radio>
                <Radio value="PRODUCTS_AND_VARIANTS">Products and Variants</Radio>
              </Radio.Group>
            </div>

            {/* Filter Type Selection */}
            <div>
              <Text strong>Time filter type:</Text>
              <br />
              <Radio.Group
                value={filterType}
                onChange={handleFilterTypeChange}
                style={{ marginTop: 8 }}
              >
                <Radio value="created">Filter by product creation time</Radio>
                <Radio value="updated">Filter by product last updated time</Radio>
              </Radio.Group>
            </div>

            {/* Date Range Filter */}
            <div>
              <Text strong>
                {filterType === 'created' 
                  ? 'Product creation time range (optional):' 
                  : 'Product last updated time range (optional):'}
              </Text>
              <br />
              {filterType === 'created' ? (
                <RangePicker
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder={['From date', 'To date']}
                  value={createdDateRange}
                  onChange={handleCreatedDateRangeChange}
                  showTime={false}
                  format="YYYY-MM-DD"
                />
              ) : (
                <RangePicker
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder={['From date', 'To date']}
                  value={updatedDateRange}
                  onChange={handleUpdatedDateRangeChange}
                  showTime={false}
                  format="YYYY-MM-DD"
                />
              )}
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Leave empty to sync all products
              </Text>
            </div>

            {/* Shopify Store Selection */}
            <div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col>
                  <Button size="small" onClick={() => setSelectedIds(shopifyStores.map(store => store.id))}>
                    Select all
                  </Button>
                </Col>
                <Col>
                  <Button size="small" onClick={() => setSelectedIds([])}>
                    Deselect all
                  </Button>
                </Col>
              </Row>
              <Checkbox.Group
                style={{ width: '100%' }}
                value={selectedIds}
                onChange={onChange}
              >
                <List
                  dataSource={shopifyStores}
                  renderItem={item => (
                    <List.Item>
                      <Checkbox value={item.id}>{item.storeName} ({item.shopifyDomain})</Checkbox>
                    </List.Item>
                  )}
                />
              </Checkbox.Group>
            </div>
          </Space>
        </>
      )}
    </Modal>
  );
};

export default SyncShopifyProductsModal;
